import React, { useEffect, useReducer, useRef } from 'react';
import { DevTool } from '@hookform/devtools';
import { BsTrash } from 'react-icons/bs';
import * as S from '../styles';
import { ActionItem, Card } from '../../../@types/domain';
import { useActivityItemForm, useRemoveActivityItemMutation } from './hooks';
import { useQueryClient } from '@tanstack/react-query';
import { errorNoticeDefault } from '../../../utils/notice';

const isInDevelopment = process.env.NODE_ENV === 'development';

type TextNoteItemProperties = {
  card: Card;
  allowEdit?: boolean;
  allowRemove?: boolean;
  item: Omit<ActionItem, 'icon'>;
  index: number;
};

const TextNoteItem = ({
  allowEdit,
  allowRemove,
  card,
  item: { disabled, text },
  index,
}: TextNoteItemProperties) => {
  const inputReference = useRef<HTMLInputElement | null>(null);
  const [inEditMode, toggleInEditMode] = useReducer((state) => !state, false);

  const queryClient = useQueryClient();

  const { register, formState, isLoading, onSubmit, reset, control } =
    useActivityItemForm(card, index, text, {
      onSuccess() {
        toggleInEditMode();
        reset();
        return queryClient.invalidateQueries(['getPlatformItemsByIds']);
      },
      onError(error) {
        errorNoticeDefault(error);
      },
    });
  const { ref, ...rest } = register('text');
  const { errors, isValid } = formState;

  const { mutateAsync: removeActionItem, isLoading: isRemovingActionItem } =
    useRemoveActivityItemMutation(card);

  useEffect(() => {
    const inputElement = inputReference.current;
    let timeoutToken;
    if (inputElement && inEditMode) {
      timeoutToken = setTimeout(() => {
        inputElement.focus();
        timeoutToken = null;
      }, 600);
    }
    return () => {
      if (timeoutToken) {
        clearTimeout(timeoutToken);
      }
    };
  }, [inputReference, inEditMode]);

  function handleDeleteClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    return removeActionItem(index);
  }

  function handleTextNoteClick() {
    const allowClick = allowEdit && !inEditMode && !disabled;
    if (allowClick) {
      toggleInEditMode();
    }
  }

  return (
    <S.ActivityItem
      data-testid="textNoteItem"
      onClick={handleTextNoteClick}
      disabled={disabled}
      textNote
    >
      <S.ActivityItemForm
        onSubmit={onSubmit}
        onBlur={onSubmit}
        allowClick={allowEdit && !inEditMode}
      >
        <S.ActionItemIcon textNote>
          <S.TextNoteIcon />
        </S.ActionItemIcon>

        {inEditMode ? (
          <S.ActivityInput
            disabled={disabled || isLoading}
            hasIcon={true}
            placeholder="Type action item"
            allowEdit={allowEdit}
            allowRemove={allowRemove}
            hasError={!!errors.text}
            autoFocus={true}
            {...rest}
            name="text"
            ref={(event) => {
              ref(event);
              inputReference.current = event;
            }}
          />
        ) : (
          <S.ActivityLabel disabled={disabled} hasIcon={true} textNote>
            {text}
          </S.ActivityLabel>
        )}

        {inEditMode && (
          <S.ActivityLinkButton
            type="submit"
            disabled={disabled || isLoading || isRemovingActionItem || !isValid}
            right="20px"
            invisible
          >
            Save
          </S.ActivityLinkButton>
        )}

        {allowRemove && !inEditMode && (
          <S.ActivityLinkButton
            type="button"
            disabled={disabled || isLoading || isRemovingActionItem}
            onClick={handleDeleteClick}
            width="30px"
            right="0"
          >
            <BsTrash />
          </S.ActivityLinkButton>
        )}

        {isInDevelopment && <DevTool control={control} />}
      </S.ActivityItemForm>
    </S.ActivityItem>
  );
};

export default TextNoteItem;
